import type {
  SmartCreationBrowserDraft,
  SmartCreationReviewSources,
  SmartCreationReviewWorkspace,
} from "@adpilot/shared";
import { IconCalendar } from "@tabler/icons-react";
import { format } from "date-fns";
import { enUS, zhCN } from "date-fns/locale";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchSmartCreationReviewSources } from "@/lib/smart-creation-api";
import type { ReviewChangeHandler } from "./review-change-handler";
import {
  type ReviewScheduleError,
  reviewScheduleToday,
  scheduleDate,
  validateReviewSchedule,
} from "./review-schedule";

interface ReviewDeliverySettingsProps {
  plan?: SmartCreationBrowserDraft;
  workspace: SmartCreationReviewWorkspace;
  disabled: boolean;
  onApply: ReviewChangeHandler;
  onDirtyChange(dirty: boolean): void;
  planNameField?: ReactNode;
}

function ReviewScheduleInput({
  id,
  label,
  value,
  disabled,
  invalid,
  minimum,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  disabled: boolean;
  invalid?: boolean;
  minimum?: string;
  onChange(value: string): void;
}) {
  const { t, i18n } = useTranslation("smartCreation");
  const [open, setOpen] = useState(false);
  const selected = scheduleDate(value);
  const minimumDate = scheduleDate(minimum ?? "");
  return (
    <InputGroup>
      <InputGroupInput
        id={id}
        type="text"
        size="default"
        inputMode="numeric"
        placeholder={id === "review-end-date" ? t("review.preview.noEndPlaceholder") : "YYYY-MM-DD"}
        value={value}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      <InputGroupAddon align="inline-end">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                disabled={disabled}
                aria-label={label}
              />
            }
          >
            <IconCalendar />
          </PopoverTrigger>
          <PopoverPopup align="end" className="w-auto p-0">
            <Calendar
              mode="single"
              selected={selected}
              defaultMonth={selected ?? minimumDate}
              locale={i18n.resolvedLanguage?.startsWith("zh") ? zhCN : enUS}
              disabled={minimumDate ? { before: minimumDate } : undefined}
              onSelect={(date) => {
                if (!date) return;
                onChange(format(date, "yyyy-MM-dd"));
                setOpen(false);
              }}
              className="p-3 [--cell-size:--spacing(8)]"
            />
          </PopoverPopup>
        </Popover>
      </InputGroupAddon>
    </InputGroup>
  );
}

export function ReviewDeliverySettings({
  plan,
  workspace,
  disabled,
  onApply,
  onDirtyChange,
  planNameField,
}: ReviewDeliverySettingsProps) {
  const { t } = useTranslation("smartCreation");
  const [sources, setSources] = useState<SmartCreationReviewSources | null>(null);
  const [loading, setLoading] = useState(false);
  const planId = plan?.id;
  const shopId = plan?.shopId;
  const [start, setStart] = useState(workspace.planSettings.startDate);
  const [end, setEnd] = useState(workspace.planSettings.endDate ?? "");
  const [errors, setErrors] = useState<{
    start: ReviewScheduleError | null;
    end: ReviewScheduleError | null;
  }>({ start: null, end: null });
  const dirty =
    start !== workspace.planSettings.startDate || end !== (workspace.planSettings.endDate ?? "");
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  useEffect(() => {
    setStart(workspace.planSettings.startDate);
    setEnd(workspace.planSettings.endDate ?? "");
    setErrors({ start: null, end: null });
  }, [workspace.planSettings.startDate, workspace.planSettings.endDate]);
  useEffect(() => {
    if (!planId || !shopId) return;
    let active = true;
    setLoading(true);
    setSources(null);
    void fetchSmartCreationReviewSources(planId, shopId)
      .then((result) => {
        if (active) setSources(result);
      })
      .catch(() => {
        if (active) toast.error(t("review.parity.sourcesError"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [planId, shopId, t]);
  const selectedPortfolio = workspace.planSettings.portfolioId.effective;
  return (
    <div
      className="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-2"
      data-review-delivery-settings
    >
      <section className="flex flex-col gap-4 rounded-[10px] border border-line p-5">
        <h3 className="text-sm font-semibold">{t("review.preview.planOwnership")}</h3>
        {planNameField}
        <Field className="gap-[7px]">
          <FieldLabel>{t("review.preview.portfolioLabel")}</FieldLabel>
          <Select
            value={selectedPortfolio ?? "none"}
            disabled={disabled || loading || !sources || dirty}
            onValueChange={(value) => {
              if (value)
                void onApply([
                  {
                    operation: "set_portfolio",
                    ref: workspace.planSettings.ref,
                    portfolioId: value === "none" ? null : value,
                  },
                ]);
            }}
          >
            <SelectTrigger className="w-full" aria-label={t("review.preview.portfolioLabel")}>
              <SelectValue placeholder={t("review.delivery.noPortfolio")}>
                {sources?.portfolios.find((item) => item.id === selectedPortfolio)?.name ??
                  selectedPortfolio ??
                  t("review.delivery.noPortfolio")}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="smart-creation-preview">
              <SelectGroup>
                <SelectItem value="none">{t("review.delivery.noPortfolio")}</SelectItem>
                {selectedPortfolio &&
                !sources?.portfolios.some((item) => item.id === selectedPortfolio) ? (
                  <SelectItem value={selectedPortfolio}>{selectedPortfolio}</SelectItem>
                ) : null}
                {sources?.portfolios.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FieldDescription className="text-[11px] leading-[18px]">
            {t("review.preview.portfolioHint")}
          </FieldDescription>
        </Field>
      </section>
      <section className="flex flex-col gap-4 rounded-[10px] border border-line p-5">
        <h3 className="text-sm font-semibold">{t("review.preview.scheduleTitle")}</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field className="gap-[7px]" invalid={errors.start !== null}>
            <FieldLabel htmlFor="review-start-date">{t("review.delivery.startDate")}</FieldLabel>
            <ReviewScheduleInput
              id="review-start-date"
              label={t("review.delivery.startDate")}
              value={start}
              minimum={reviewScheduleToday(new Date())}
              disabled={disabled}
              invalid={errors.start !== null}
              onChange={(value) => {
                setStart(value);
                setErrors({ start: null, end: null });
              }}
            />
            {errors.start ? (
              <FieldError match>{t(`review.delivery.scheduleError.${errors.start}`)}</FieldError>
            ) : null}
          </Field>
          <Field className="gap-[7px]" invalid={errors.end !== null}>
            <FieldLabel htmlFor="review-end-date">{t("review.delivery.endDate")}</FieldLabel>
            <ReviewScheduleInput
              id="review-end-date"
              label={t("review.delivery.endDate")}
              value={end}
              minimum={start}
              disabled={disabled}
              invalid={errors.end !== null}
              onChange={(value) => {
                setEnd(value);
                setErrors({ start: null, end: null });
              }}
            />
            {errors.end ? (
              <FieldError match>{t(`review.delivery.scheduleError.${errors.end}`)}</FieldError>
            ) : null}
          </Field>
        </div>
        {dirty ? (
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={disabled || !dirty}
              onClick={async () => {
                const result = validateReviewSchedule(
                  start,
                  end,
                  reviewScheduleToday(new Date()),
                  plan?.draft.promotionDateRange ?? null,
                );
                if (result.start || result.end) {
                  setErrors(result);
                  return;
                }
                await onApply([
                  {
                    operation: "set_plan_schedule",
                    ref: workspace.planSettings.ref,
                    startDate: start,
                    endDate: end || null,
                  },
                ]);
              }}
            >
              {t("review.delivery.applyDates")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled || !dirty}
              onClick={() => {
                setStart(workspace.planSettings.startDate);
                setEnd(workspace.planSettings.endDate ?? "");
                setErrors({ start: null, end: null });
              }}
            >
              {t("review.parity.cancel")}
            </Button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
